import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  Check,
  CheckCircle2,
  Clock,
  Euro,
  FileText,
  Loader2,
  Paperclip,
  Pencil,
  Plus,
  Save,
  ShoppingCart,
  Ban,
  TrendingDown,
  X,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  createAchat,
  createAchatLot,
  fetchEvenementsBureau,
  annulerAchat,
  fetchAchats,
  fetchExerciceChiffres,
  updateAchat,
  uploadDocumentAchatLot,
  uploadJustificatifAchat,
  type Achat,
} from '../services/api';
import { useAuth } from '../hooks/useAuth';
import ChampFournisseur from '../components/ChampFournisseur';
import { usePersonnalisation } from '../hooks/usePersonnalisation';
import { nomEdition } from '../utils/evenement';
import { euros } from '../utils/format';

import { dateFr } from '../utils/dates';
const modesPaiement: Record<string, string> = {
  carte: 'Carte',
  virement: 'Virement',
  cheque: 'Chèque',
  especes: 'Espèces',
};

const dateCourte = new Intl.DateTimeFormat('fr-FR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});

/**
 * Suivi des achats : le pendant du suivi des ventes.
 *
 * Là où les factures émises disent ce qui rentre, les achats disent ce qui
 * sort. Le trésorier y tient sa liste — dépense, fournisseur, règlement — avec
 * le justificatif joint : c'est lui qu'on cherche à l'assemblée générale, pas
 * le montant.
 */
export default function BureauAchatsPage() {
  const { isLoading: authLoading, aLeDroit } = useAuth();
  const queryClient = useQueryClient();
  const aAcces = aLeDroit('suivre_achats');
  const perso = usePersonnalisation();
  // L'option de la plateforme : fermée, l'historique reste lisible mais la
  // saisie se verrouille — le serveur la refuse de toute façon.
  const optionOuverte = perso?.options?.achats !== false;

  const [notice, setNotice] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [ajout, setAjout] = useState(false);
  const [enEdition, setEnEdition] = useState<number | null>(null);

  const query = useQuery({ queryKey: ['bureau-achats'], queryFn: fetchAchats, enabled: aAcces });
  const chiffresQuery = useQuery({
    queryKey: ['bureau-exercice-chiffres'],
    queryFn: fetchExerciceChiffres,
    enabled: aAcces,
  });
  const exerciceRouvert = chiffresQuery.data?.rouvert ?? null;
  const evenementsQuery = useQuery({
    queryKey: ['bureau-evenements'],
    queryFn: fetchEvenementsBureau,
    enabled: aAcces,
  });

  const rafraichir = (message: string) => {
    setNotice({ type: 'success', message });
    queryClient.invalidateQueries({ queryKey: ['bureau-achats'] });
  };
  const echec = (e: Error) => setNotice({ type: 'error', message: e.message });

  const supprimer = useMutation({
    mutationFn: ({ id, motif }: { id: number; motif: string }) => annulerAchat(id, motif),
    onSuccess: (d: any) => rafraichir(d.message),
    onError: echec,
  });

  const basculerStatut = useMutation({
    mutationFn: ({ id, statut }: { id: number; statut: string }) => updateAchat(id, { statut }),
    onSuccess: (d: any) => rafraichir(d.message),
    onError: echec,
  });

  if (authLoading || (aAcces && query.isPending)) {
    return (
      <div className="p-8 flex justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (!aAcces) {
    return (
      <div className="p-4 lg:p-0 pb-24 lg:pb-0">
        <div className="card bg-red-50 border-red-200 text-red-800 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 mt-0.5" />
          <div>
            <p className="font-semibold">Accès réservé</p>
            <p className="text-sm mt-1">
              Le suivi des achats dépend de votre fonction au bureau : un administrateur
              peut l'ouvrir dans la grille de droits.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const achats = query.data?.achats ?? [];
  const evenements: { id: number; nom: string }[] = evenementsQuery.data?.evenements ?? [];
  const totaux = query.data?.totaux;
  const annee = query.data?.annee ?? new Date().getFullYear();

  return (
    <div className="p-4 lg:p-0 space-y-5 lg:space-y-6 pb-24 lg:pb-0">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <ShoppingCart className="w-7 h-7 text-primary-700" />
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-gray-100">
              Suivi des achats
            </h1>
            <p className="text-sm lg:text-base text-gray-600 dark:text-gray-400">
              Les dépenses de l'association, avec leurs justificatifs.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Link
            to="/bureau/achats/previsionnel"
            className="btn-outline inline-flex items-center gap-2"
          >
            <TrendingDown className="w-4 h-4" /> Prévisionnel
          </Link>
          <button
            type="button"
            onClick={() => {
              setAjout(!ajout);
              setEnEdition(null);
              setNotice(null);
            }}
            disabled={!optionOuverte}
            className="btn-primary inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Plus className="w-4 h-4" /> Nouvel achat
          </button>
        </div>
      </div>

      {/* Pendant une fenêtre de correction, un achat daté dans l'année
          rouverte compte dans son bilan : le dire ici évite de chercher un
          réglage qui n'existe pas — la date fait tout. */}
      {exerciceRouvert && (
        <div className="card bg-primary-50 border-primary-200 text-primary-900 dark:bg-primary-900/20 dark:border-primary-800 dark:text-primary-200 text-sm">
          L'exercice <strong>{exerciceRouvert.annee}</strong> est rouvert pour correction
          {exerciceRouvert.jusquA ? ` jusqu'au ${dateFr(exerciceRouvert.jusquA)}` : ''} :
          un achat daté en {exerciceRouvert.annee} entrera dans son bilan.
        </div>
      )}

      {!optionOuverte && (
        <div className="card bg-amber-50 border-amber-200 text-amber-800 dark:bg-amber-900/20 dark:border-amber-800 dark:text-amber-200 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 mt-0.5 shrink-0" />
          <p className="text-sm">
            <strong>Module non inclus</strong> — l'historique reste consultable, mais la saisie
            est fermée. Un administrateur peut souscrire le module Suivi des achats depuis{' '}
            <Link to="/admin/parametres" className="underline font-medium">Paramètres → Modules</Link>.
          </p>
        </div>
      )}

      {/* Les trois chiffres qu'on vient chercher : ce que l'année a coûté, la
          comparaison, et ce qui reste à sortir. */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="card py-4 text-center">
          <p className="text-xs text-gray-500 dark:text-gray-400">Dépensé {annee}</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-gray-100 tabular-nums">
            {euros.format(totaux?.depenseAnnee ?? 0)}
          </p>
        </div>
        <div className="card py-4 text-center">
          <p className="text-xs text-gray-500 dark:text-gray-400 inline-flex items-center gap-1">
            <TrendingDown className="w-3.5 h-3.5" /> Dépensé {annee - 1}
          </p>
          <p className="text-2xl font-bold text-gray-700 dark:text-gray-300 tabular-nums">
            {euros.format(totaux?.depenseAnneePrecedente ?? 0)}
          </p>
        </div>
        <div className="card py-4 text-center">
          <p className="text-xs text-gray-500 dark:text-gray-400">Reste à payer</p>
          <p
            className={`text-2xl font-bold tabular-nums ${
              (totaux?.resteAPayer ?? 0) > 0 ? 'text-amber-600' : 'text-green-600'
            }`}
          >
            {euros.format(totaux?.resteAPayer ?? 0)}
          </p>
        </div>
      </div>

      {notice && (
        <div
          className={`card flex items-start gap-3 text-sm ${
            notice.type === 'success'
              ? 'bg-green-50 border-green-200 text-green-800'
              : 'bg-red-50 border-red-200 text-red-800'
          }`}
        >
          {notice.type === 'success' ? (
            <Check className="w-4 h-4 mt-0.5 shrink-0" />
          ) : (
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
          )}
          <p className="flex-1">{notice.message}</p>
          <button onClick={() => setNotice(null)} className="text-xs underline">Fermer</button>
        </div>
      )}

      {/* La saisie d'un nouvel achat se fait en lot : un passage en caisse,
          plusieurs lignes, l'en-tête commun et les justificatifs du ticket. */}
      {ajout && (
        <FormulaireLotAchat
          libelles={[...new Set(achats.map((a) => a.libelle))].sort()}
          achats={achats}
          evenements={evenements}
          onFini={(message) => {
            setAjout(false);
            rafraichir(message);
          }}
          onEchec={echec}
          onAnnuler={() => setAjout(false)}
        />
      )}

      {enEdition !== null && (
        <FormulaireAchat
          achat={achats.find((a) => a.id === enEdition)}
          libelles={[...new Set(achats.map((a) => a.libelle))].sort()}
          achats={achats}
          evenements={evenements}
          onFini={(message) => {
            setEnEdition(null);
            rafraichir(message);
          }}
          onEchec={echec}
          onAnnuler={() => setEnEdition(null)}
        />
      )}

      {achats.length === 0 && !ajout ? (
        <div className="card lg:p-8 text-center space-y-3">
          <ShoppingCart className="w-10 h-10 mx-auto text-gray-300" />
          <p className="text-gray-600 dark:text-gray-400">
            Aucun achat enregistré. Saisissez les dépenses de l'association pour suivre ce
            qui sort, comme les ventes disent ce qui rentre.
          </p>
        </div>
      ) : (
        <section className="card p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-800 text-left text-gray-600 dark:text-gray-300">
              <tr>
                <th className="px-4 py-3 font-medium">Date</th>
                <th className="px-4 py-3 font-medium">Libellé</th>
                <th className="px-4 py-3 font-medium">Fournisseur</th>
                <th className="px-4 py-3 font-medium text-center">Qté</th>
                <th className="px-4 py-3 font-medium text-right">Montant</th>
                <th className="px-4 py-3 font-medium">Règlement</th>
                <th className="px-4 py-3 font-medium text-center">Justif.</th>
                <th className="px-4 py-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
              {achats.map((a) => (
                <LigneAchat
                  key={a.id}
                  achat={a}
                  onEditer={() => {
                    setAjout(false);
                    setEnEdition(a.id);
                  }}
                  onSupprimer={() => {
                    /*
                      Une dépense s'annule, elle ne s'efface pas.

                      L'effacement emportait le JUSTIFICATIF avec elle — la
                      facture du fournisseur, sur le disque comme en base.
                      C'est la pièce que réclamerait un contrôle, et elle
                      partait sans laisser de trace de son passage.

                      Le motif est demandé : « saisie en double » et « commande
                      annulée » ne se rattrapent pas de la même façon.
                    */
                    const motif = window.prompt(
                      `Annuler la dépense « ${a.libelle} » ?\n\n` +
                        'Elle restera visible, barrée, avec son justificatif. Dites pourquoi :',
                    );
                    if (motif !== null && motif.trim() !== '') {
                      supprimer.mutate({ id: a.id, motif: motif.trim() });
                    }
                  }}
                  onBasculerStatut={() =>
                    basculerStatut.mutate({
                      id: a.id,
                      statut: a.statut === 'paye' ? 'a_payer' : 'paye',
                    })
                  }
                  onJustificatif={rafraichir}
                  onEchec={echec}
                />
              ))}
            </tbody>
          </table>
        </section>
      )}
    </div>
  );
}

function LigneAchat({
  achat: a,
  onEditer,
  onSupprimer,
  onBasculerStatut,
  onJustificatif,
  onEchec,
}: {
  achat: Achat;
  onEditer: () => void;
  onSupprimer: () => void;
  onBasculerStatut: () => void;
  onJustificatif: (message: string) => void;
  onEchec: (e: Error) => void;
}) {
  const deposer = useMutation({
    mutationFn: (fichier: File) => uploadJustificatifAchat(a.id, fichier),
    onSuccess: (d: any) => onJustificatif(d.message),
    onError: onEchec,
  });

  /*
    Une dépense annulée reste dans la liste, en gris et barrée.

    L'effacer la rendrait invisible, ce qui reviendrait à l'avoir supprimée du
    point de vue de qui regarde l'écran. Tout l'intérêt de ne pas la détruire
    est qu'on la VOIE — avec son justificatif, son motif et le nom de qui l'a
    annulée. Elle ne compte plus dans aucun total.
  */
  const annulee = a.annule === true;

  return (
    <tr className={annulee ? 'opacity-60' : undefined}>
      <td className="px-4 py-3 whitespace-nowrap tabular-nums">
        {dateCourte.format(new Date(a.dateAchat))}
      </td>
      <td className="px-4 py-3">
        <span
          className={
            annulee
              ? 'font-medium text-gray-500 dark:text-gray-400 line-through'
              : 'font-medium text-gray-900 dark:text-gray-100'
          }
        >
          {a.libelle}
        </span>
        {annulee && (
          <span className="block text-xs text-red-700 dark:text-red-400">
            Annulée{a.annulePar ? ` par ${a.annulePar}` : ''}
            {a.motifAnnulation ? ` — ${a.motifAnnulation}` : ''}
          </span>
        )}
        {a.evenement && (
          <span className="block text-xs text-primary-700 dark:text-primary-400">{a.evenement.nom}</span>
        )}
        {a.commentaire && (
          <span className="block text-xs text-gray-500 dark:text-gray-400">{a.commentaire}</span>
        )}
      </td>
      <td className="px-4 py-3 text-gray-600 dark:text-gray-400">{a.fournisseur ?? '—'}</td>
      <td className="px-4 py-3 text-center tabular-nums">
        {a.quantite}
        {a.unite && <span className="text-xs text-gray-500 dark:text-gray-400"> {a.unite}</span>}
        {a.prixUnitaire != null && (
          <span className="block text-[10px] text-gray-400">× {euros.format(a.prixUnitaire)}</span>
        )}
      </td>
      <td className="px-4 py-3 text-right tabular-nums font-medium">{euros.format(a.montant)}</td>
      <td className="px-4 py-3">
        {/* Le statut se bascule d'un clic : marquer un achat payé est le geste
            le plus fréquent de l'écran, il ne mérite pas un formulaire. */}
        <button
          type="button"
          onClick={onBasculerStatut}
          title={a.statut === 'paye' ? 'Repasser en attente' : 'Marquer payé'}
          className={`text-xs px-2 py-1 rounded-full font-medium inline-flex items-center gap-1 ${
            a.statut === 'paye'
              ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200'
              : 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200'
          }`}
        >
          {a.statut === 'paye' ? (
            <CheckCircle2 className="w-3 h-3" />
          ) : (
            <Clock className="w-3 h-3" />
          )}
          {a.statut === 'paye' ? 'Payé' : 'À payer'}
        </button>
        {a.modePaiement && (
          <span className="block text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            {modesPaiement[a.modePaiement] ?? a.modePaiement}
          </span>
        )}
      </td>
      <td className="px-4 py-3 text-center">
        {(a.documents?.length ?? 0) > 0 ? (
          // Les pièces du lot : le ticket porte tout le panier, chaque
          // document s'ouvre d'un clic.
          <span className="inline-flex gap-1">
            {a.documents.map((d) => (
              <a
                key={d.id}
                href={d.url}
                target="_blank"
                rel="noopener noreferrer"
                title={d.nom}
                className="inline-flex text-primary-700 hover:text-primary-800"
              >
                <FileText className="w-4 h-4" />
              </a>
            ))}
          </span>
        ) : a.justificatifUrl ? (
          <a
            href={a.justificatifUrl}
            target="_blank"
            rel="noopener noreferrer"
            title="Ouvrir le justificatif"
            className="inline-flex text-primary-700 hover:text-primary-800"
          >
            <FileText className="w-4 h-4" />
          </a>
        ) : (
          <label
            title="Joindre le justificatif"
            className="inline-flex cursor-pointer text-gray-300 hover:text-primary-600"
          >
            {deposer.isPending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Paperclip className="w-4 h-4" />
            )}
            <input
              type="file"
              accept="application/pdf,image/png,image/jpeg,image/webp"
              className="hidden"
              onChange={(e) => {
                const fichier = e.target.files?.[0];
                if (fichier) deposer.mutate(fichier);
                e.target.value = '';
              }}
            />
          </label>
        )}
      </td>
      <td className="px-4 py-3 text-right whitespace-nowrap">
        <button
          type="button"
          onClick={onEditer}
          title="Modifier"
          className="p-1.5 rounded-lg text-gray-400 hover:text-primary-700 hover:bg-primary-50 dark:hover:bg-gray-700"
        >
          <Pencil className="w-4 h-4" />
        </button>
        {/*
          Une ligne déjà annulée n'offre plus le bouton : une annulation ne se
          défait pas, et proposer un geste qui sera refusé n'apprend rien.
        */}
        {!annulee && (
          <button
            type="button"
            onClick={onSupprimer}
            title="Annuler cette dépense"
            className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
          >
            <Ban className="w-4 h-4" />
          </button>
        )}
      </td>
    </tr>
  );
}

function FormulaireAchat({
  achat,
  libelles,
  achats,
  evenements,
  onFini,
  onEchec,
  onAnnuler,
}: {
  achat?: Achat;
  /** Libellés déjà saisis : la liste évite de retaper le même article. */
  libelles: string[];
  /** Tous les achats : la liaison libellé → fournisseurs s'en déduit. */
  achats: Achat[];
  evenements: { id: number; nom: string }[];
  onFini: (message: string) => void;
  onEchec: (e: Error) => void;
  onAnnuler: () => void;
}) {
  const [form, setForm] = useState({
    libelle: achat?.libelle ?? '',
    fournisseur: achat?.fournisseur ?? '',
    montant: achat?.montant ? String(achat.montant) : '',
    dateAchat: achat?.dateAchat ?? new Date().toISOString().slice(0, 10),
    quantite: achat?.quantite ? String(achat.quantite) : '1',
    evenementId: achat?.evenement?.id ? String(achat.evenement.id) : '',
    modePaiement: achat?.modePaiement ?? '',
    statut: achat?.statut ?? 'a_payer',
    commentaire: achat?.commentaire ?? '',
  });
  const [justificatif, setJustificatif] = useState<File | null>(null);

  // Les fournisseurs déjà associés au libellé saisi, à défaut tous ceux
  // connus : la liaison se déduit des achats, sans table de plus.
  const duLibelle = [
    ...new Set(
      achats
        .filter((a) => a.fournisseur && a.libelle.trim().toLowerCase() === form.libelle.trim().toLowerCase())
        .map((a) => a.fournisseur as string)
    ),
  ].sort();
  // La liste de tous les fournisseurs déjà tapés ne sert plus : le champ
  // interroge désormais le répertoire, qui les connaît sous leur nom exact.
  // Seuls restent ceux liés au libellé saisi — ce que le répertoire ne peut
  // pas savoir, et que seuls les achats passés disent.

  const enregistrer = useMutation({
    mutationFn: async () => {
      const payload = {
        ...form,
        montant: Number(form.montant),
        quantite: Number(form.quantite) || 1,
        evenementId: form.evenementId === '' ? null : Number(form.evenementId),
      };

      const reponse: any = achat ? await updateAchat(achat.id, payload) : await createAchat(payload);

      // La piece choisie part dans la foulee, sur l'identifiant que la
      // creation vient de rendre : saisir l'achat et joindre sa facture est
      // un seul geste, pas deux.
      if (justificatif && reponse?.achat?.id) {
        try {
          await uploadJustificatifAchat(reponse.achat.id, justificatif);
        } catch (e) {
          // L'achat est enregistre, seule la piece a echoue : le dire tel
          // quel — le trombone de la ligne permet de reessayer.
          throw new Error(
            `Achat enregistré, mais le justificatif n'a pas pu être déposé (${(e as Error).message}). Réessayez depuis le trombone de la ligne.`
          );
        }
      }

      return reponse;
    },
    onSuccess: (d: any) => onFini(d.message),
    onError: onEchec,
  });

  const valide = form.libelle.trim() !== '' && Number(form.montant) > 0;

  return (
    <section className="card lg:p-5 space-y-3 border-2 border-primary-200 dark:border-primary-800">
      <div className="flex items-center justify-between gap-2">
        <h2 className="font-semibold text-gray-900 dark:text-gray-100">
          {achat ? `Modifier « ${achat.libelle} »` : 'Nouvel achat'}
        </h2>
        <button
          type="button"
          onClick={onAnnuler}
          className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600"
          aria-label="Annuler"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="sm:col-span-2">
          {/* Les libellés déjà saisis se proposent à la frappe : le même
              article se retape à l'identique, et le prévisionnel peut ensuite
              rapprocher les éditions ligne à ligne. */}
          <input
            className="input w-full"
            list="libelles-achats"
            placeholder="Libellé * — ex. Fûts bière fête votive"
            value={form.libelle}
            onChange={(e) => setForm({ ...form, libelle: e.target.value })}
          />
          <datalist id="libelles-achats">
            {libelles.map((l) => (
              <option key={l} value={l} />
            ))}
          </datalist>
        </div>
        <div>
          {/* Le champ interroge le répertoire des fournisseurs. Il ne
              proposait que les noms déjà tapés sur d'autres achats : « Brasserie
              du Coin » et « brasserie du coin » comptaient pour deux, et le
              rattachement des achats aux fiches — qui se fait par
              correspondance de nom — échouait sur l'un des deux. */}
          <ChampFournisseur
            valeur={form.fournisseur}
            onChange={(nom) => setForm({ ...form, fournisseur: nom })}
          />

          {/* Ceux chez qui on a déjà pris cet article : le répertoire ne le
              sait pas, seuls les achats passés le disent. */}
          {form.fournisseur.trim() === '' && duLibelle.length > 0 && (
            <p className="text-[0.7rem] text-gray-500 dark:text-gray-400 mt-1">
              Déjà pris chez{' '}
              {duLibelle.slice(0, 3).map((f, i) => (
                <button
                  key={f}
                  type="button"
                  className="underline hover:text-primary-700"
                  onClick={() => setForm({ ...form, fournisseur: f })}
                >
                  {f}
                  {i < Math.min(duLibelle.length, 3) - 1 ? ', ' : ''}
                </button>
              ))}
            </p>
          )}
        </div>
        <div className="relative">
          <input
            className="input pr-8"
            type="number"
            min={1}
            placeholder="Montant *"
            value={form.montant}
            onChange={(e) => setForm({ ...form, montant: e.target.value })}
          />
          <Euro className="w-4 h-4 text-gray-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
        </div>
        <label className="text-sm text-gray-600 dark:text-gray-400 block">
          Quantité
          <input
            className="input mt-1"
            type="number"
            min={1}
            value={form.quantite}
            onChange={(e) => setForm({ ...form, quantite: e.target.value })}
          />
          <span className="text-xs text-gray-500 dark:text-gray-400">
            Douze fûts, trois cents écocups : c'est elle qui fait le prévisionnel.
          </span>
        </label>
        <label className="text-sm text-gray-600 dark:text-gray-400 block">
          Événement rattaché
          <select
            className="input mt-1"
            value={form.evenementId}
            onChange={(e) => setForm({ ...form, evenementId: e.target.value })}
          >
            <option value="">Aucun — dépense générale</option>
            {evenements.map((ev) => (
              <option key={ev.id} value={ev.id}>{nomEdition(ev.nom, (ev as any).dateDebut)}</option>
            ))}
          </select>
        </label>
        <label className="text-sm text-gray-600 dark:text-gray-400 block">
          Date de l'achat
          <input
            className="input mt-1"
            type="date"
            value={form.dateAchat}
            onChange={(e) => setForm({ ...form, dateAchat: e.target.value })}
          />
        </label>
        <label className="text-sm text-gray-600 dark:text-gray-400 block">
          Mode de paiement
          <select
            className="input mt-1"
            value={form.modePaiement}
            onChange={(e) => setForm({ ...form, modePaiement: e.target.value })}
          >
            <option value="">—</option>
            {Object.entries(modesPaiement).map(([valeur, label]) => (
              <option key={valeur} value={valeur}>{label}</option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 sm:col-span-2 cursor-pointer">
          <input
            type="checkbox"
            checked={form.statut === 'paye'}
            onChange={(e) => setForm({ ...form, statut: e.target.checked ? 'paye' : 'a_payer' })}
            className="w-4 h-4"
          />
          Déjà payé
        </label>
        <textarea
          className="input sm:col-span-2 min-h-[3rem]"
          placeholder="Commentaire"
          rows={2}
          value={form.commentaire}
          onChange={(e) => setForm({ ...form, commentaire: e.target.value })}
        />

        {/* La facture se joint dès la saisie : la scanner plus tard, on ne le
            fait jamais. */}
        <div className="sm:col-span-2">
          <label className="inline-flex items-center gap-2 text-sm text-primary-700 dark:text-primary-400 cursor-pointer hover:underline">
            <Paperclip className="w-4 h-4" />
            {justificatif
              ? justificatif.name
              : achat?.justificatifUrl
                ? 'Remplacer le justificatif joint'
                : 'Joindre la facture ou le ticket (PDF ou photo)'}
            <input
              type="file"
              accept="application/pdf,image/png,image/jpeg,image/webp"
              className="hidden"
              onChange={(e) => setJustificatif(e.target.files?.[0] ?? null)}
            />
          </label>
          {justificatif && (
            <button
              type="button"
              onClick={() => setJustificatif(null)}
              className="ml-2 text-xs text-gray-500 dark:text-gray-400 underline"
            >
              Retirer
            </button>
          )}
        </div>
      </div>

      <button
        type="button"
        onClick={() => enregistrer.mutate()}
        disabled={enregistrer.isPending || !valide}
        className="btn-primary inline-flex items-center gap-2 disabled:opacity-50"
      >
        {enregistrer.isPending ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <Save className="w-4 h-4" />
        )}
        {achat ? 'Enregistrer' : 'Ajouter l\'achat'}
      </button>
    </section>
  );
}

type LigneSaisie = { libelle: string; quantite: string; unite: string; prixUnitaire: string; montant: string };

const ligneVierge: LigneSaisie = { libelle: '', quantite: '', unite: '', prixUnitaire: '', montant: '' };

/**
 * Saisie d'un nouvel achat, en lot : un passage en caisse.
 *
 * L'en-tête — date et « déjà payé », puis événement, fournisseur et moyen de
 * paiement — vaut pour toutes les lignes. Chaque ligne porte son libellé, sa
 * quantité, son unité, son PU et son montant : quand quantité et PU sont
 * renseignés, le montant se calcule tout seul. Les justificatifs — la facture
 * ET le ticket, PDF ou photos — se listent en cartes avant l'envoi.
 */
function FormulaireLotAchat({
  libelles,
  achats,
  evenements,
  onFini,
  onEchec,
  onAnnuler,
}: {
  libelles: string[];
  achats: Achat[];
  evenements: { id: number; nom: string }[];
  onFini: (message: string) => void;
  onEchec: (e: Error) => void;
  onAnnuler: () => void;
}) {
  const [entete, setEntete] = useState({
    dateAchat: new Date().toISOString().slice(0, 10),
    statut: 'a_payer' as 'a_payer' | 'paye',
    evenementId: '',
    fournisseur: '',
    modePaiement: '',
    commentaire: '',
  });
  const [lignes, setLignes] = useState<LigneSaisie[]>([{ ...ligneVierge }]);
  const [documents, setDocuments] = useState<File[]>([]);

  const tousFournisseurs = [
    ...new Set(achats.map((a) => a.fournisseur).filter((f): f is string => Boolean(f))),
  ].sort();

  // Le montant suit quantité × PU dès que les deux sont là ; sinon il reste
  // libre — un forfait s'inscrit sans détail.
  const changerLigne = (index: number, champ: keyof LigneSaisie, valeur: string) => {
    setLignes((prev) =>
      prev.map((l, i) => {
        if (i !== index) return l;
        const suivante = { ...l, [champ]: valeur };
        const quantite = parseFloat(suivante.quantite);
        const pu = parseFloat(suivante.prixUnitaire);
        if ((champ === 'quantite' || champ === 'prixUnitaire') && quantite > 0 && pu > 0) {
          suivante.montant = String(Math.round(quantite * pu));
        }
        return suivante;
      })
    );
  };

  const lignesValides = lignes.filter((l) => l.libelle.trim() !== '' && Number(l.montant) > 0);
  const total = lignesValides.reduce((s, l) => s + Number(l.montant), 0);
  const valide =
    lignesValides.length > 0 &&
    lignesValides.length === lignes.filter((l) => l.libelle.trim() !== '' || l.montant !== '').length;

  const enregistrer = useMutation({
    mutationFn: async () => {
      const reponse = await createAchatLot({
        dateAchat: entete.dateAchat,
        statut: entete.statut,
        evenementId: entete.evenementId === '' ? null : Number(entete.evenementId),
        fournisseur: entete.fournisseur,
        modePaiement: entete.modePaiement,
        commentaire: entete.commentaire,
        lignes: lignesValides.map((l) => ({
          libelle: l.libelle.trim(),
          quantite: Number(l.quantite) || 1,
          unite: l.unite.trim() || undefined,
          prixUnitaire: l.prixUnitaire === '' ? null : Number(l.prixUnitaire),
          montant: Number(l.montant),
        })),
      });

      // Les pièces partent dans la foulée, sur le lot que la création vient
      // de rendre : saisir et joindre est un seul geste.
      const rates: string[] = [];
      for (const fichier of documents) {
        try {
          await uploadDocumentAchatLot(reponse.lot, fichier);
        } catch {
          rates.push(fichier.name);
        }
      }
      if (rates.length > 0) {
        throw new Error(
          `${reponse.message} Mais ${rates.length} document(s) n'ont pas pu être déposés (${rates.join(', ')}).`
        );
      }

      return reponse;
    },
    onSuccess: (d: any) => onFini(d.message),
    onError: onEchec,
  });

  return (
    <section className="card lg:p-5 space-y-4 border-2 border-primary-200 dark:border-primary-800">
      <div className="flex items-center justify-between gap-2">
        <h2 className="font-semibold text-gray-900 dark:text-gray-100">Nouvel achat</h2>
        <button type="button" onClick={onAnnuler} className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600" aria-label="Annuler">
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* En-tête, rang 1 : la date, et « déjà payé » en vis-à-vis. */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <label className="text-sm text-gray-600 dark:text-gray-400 block">
          Date
          <input
            className="input mt-1"
            type="date"
            value={entete.dateAchat}
            onChange={(e) => setEntete({ ...entete, dateAchat: e.target.value })}
          />
        </label>
        <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer pb-2.5">
          <input
            type="checkbox"
            checked={entete.statut === 'paye'}
            onChange={(e) => setEntete({ ...entete, statut: e.target.checked ? 'paye' : 'a_payer' })}
            className="w-4 h-4"
          />
          Déjà payé
        </label>
      </div>

      {/* En-tête, rang 2 : événement, fournisseur, moyen de paiement. */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <label className="text-sm text-gray-600 dark:text-gray-400 block">
          Événement
          <select
            className="input mt-1"
            value={entete.evenementId}
            onChange={(e) => setEntete({ ...entete, evenementId: e.target.value })}
          >
            <option value="">Aucun — dépense générale</option>
            {evenements.map((ev) => (
              <option key={ev.id} value={ev.id}>{nomEdition(ev.nom, (ev as any).dateDebut)}</option>
            ))}
          </select>
        </label>
        <label className="text-sm text-gray-600 dark:text-gray-400 block">
          Fournisseur
          <input
            className="input mt-1"
            list="fournisseurs-lot"
            value={entete.fournisseur}
            onChange={(e) => setEntete({ ...entete, fournisseur: e.target.value })}
          />
          <datalist id="fournisseurs-lot">
            {tousFournisseurs.map((f) => (
              <option key={f} value={f} />
            ))}
          </datalist>
        </label>
        <label className="text-sm text-gray-600 dark:text-gray-400 block">
          Moyen de paiement
          <select
            className="input mt-1"
            value={entete.modePaiement}
            onChange={(e) => setEntete({ ...entete, modePaiement: e.target.value })}
          >
            <option value="">—</option>
            {Object.entries(modesPaiement).map(([valeur, label]) => (
              <option key={valeur} value={valeur}>{label}</option>
            ))}
          </select>
        </label>
      </div>

      {/* Les lignes du panier. */}
      <div className="space-y-2">
        <div className="hidden sm:grid grid-cols-[1fr_5rem_6rem_6rem_6rem_2rem] gap-2 text-xs text-gray-500 dark:text-gray-400 px-1">
          <span>Libellé *</span>
          <span>Qté</span>
          <span>Unité</span>
          <span>PU €</span>
          <span>Montant € *</span>
          <span />
        </div>
        {lignes.map((l, index) => (
          <div key={index} className="grid grid-cols-2 sm:grid-cols-[1fr_5rem_6rem_6rem_6rem_2rem] gap-2 items-center">
            <input
              className="input col-span-2 sm:col-span-1"
              list="libelles-achats-lot"
              placeholder="Libellé — ex. Fûts bière"
              value={l.libelle}
              onChange={(e) => changerLigne(index, 'libelle', e.target.value)}
            />
            <input
              className="input"
              type="number"
              min={0}
              placeholder="Qté"
              value={l.quantite}
              onChange={(e) => changerLigne(index, 'quantite', e.target.value)}
            />
            <input
              className="input"
              placeholder="fûts, kg…"
              value={l.unite}
              onChange={(e) => changerLigne(index, 'unite', e.target.value)}
            />
            <input
              className="input"
              type="number"
              min={0}
              step="0.01"
              placeholder="PU"
              value={l.prixUnitaire}
              onChange={(e) => changerLigne(index, 'prixUnitaire', e.target.value)}
            />
            <input
              className={`input ${Number(l.quantite) > 0 && Number(l.prixUnitaire) > 0 ? 'bg-gray-50 dark:bg-gray-800' : ''}`}
              type="number"
              min={1}
              placeholder="Montant"
              title={Number(l.quantite) > 0 && Number(l.prixUnitaire) > 0 ? 'Calculé : quantité × PU' : undefined}
              value={l.montant}
              readOnly={Number(l.quantite) > 0 && Number(l.prixUnitaire) > 0}
              onChange={(e) => changerLigne(index, 'montant', e.target.value)}
            />
            <button
              type="button"
              onClick={() => setLignes((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== index) : prev))}
              disabled={lignes.length === 1}
              title="Retirer la ligne"
              className="p-1.5 rounded-lg text-gray-300 hover:text-red-600 disabled:opacity-30 justify-self-end"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ))}
        <datalist id="libelles-achats-lot">
          {libelles.map((l) => (
            <option key={l} value={l} />
          ))}
        </datalist>
        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={() => setLignes((prev) => [...prev, { ...ligneVierge }])}
            className="btn-outline inline-flex items-center gap-1.5 text-sm px-3 py-1.5"
          >
            <Plus className="w-4 h-4" /> Ajouter une ligne
          </button>
          {total > 0 && (
            <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 tabular-nums">
              Total : {euros.format(total)}
            </p>
          )}
        </div>
      </div>

      <textarea
        className="input w-full min-h-[3rem]"
        placeholder="Commentaire"
        rows={2}
        value={entete.commentaire}
        onChange={(e) => setEntete({ ...entete, commentaire: e.target.value })}
      />

      {/* Les justificatifs : la facture ET le ticket, PDF ou photos. */}
      <div className="space-y-2">
        <label className="inline-flex items-center gap-2 text-sm text-primary-700 dark:text-primary-400 cursor-pointer hover:underline">
          <Paperclip className="w-4 h-4" />
          Ajouter un document — la facture ou le ticket (PDF ou photo)
          <input
            type="file"
            accept="application/pdf,image/png,image/jpeg,image/webp"
            multiple
            className="hidden"
            onChange={(e) => {
              const fichiers = Array.from(e.target.files ?? []);
              if (fichiers.length > 0) setDocuments((prev) => [...prev, ...fichiers]);
              e.target.value = '';
            }}
          />
        </label>
        {documents.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
            {documents.map((f, index) => (
              <div
                key={`${f.name}-${index}`}
                className="rounded-xl border border-gray-200 dark:border-gray-700 p-3 flex items-start gap-2"
              >
                <FileText className="w-5 h-5 text-primary-600 shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-gray-900 dark:text-gray-100 truncate" title={f.name}>{f.name}</p>
                  <p className="text-[10px] text-gray-400">{(f.size / 1024).toFixed(0)} Ko</p>
                </div>
                <button
                  type="button"
                  onClick={() => setDocuments((prev) => prev.filter((_, i) => i !== index))}
                  className="text-gray-300 hover:text-red-600 shrink-0"
                  title="Retirer"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <button
        type="button"
        onClick={() => enregistrer.mutate()}
        disabled={enregistrer.isPending || !valide}
        className="btn-primary inline-flex items-center gap-2 disabled:opacity-50"
      >
        {enregistrer.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
        Enregistrer l'achat{lignesValides.length > 1 ? ` (${lignesValides.length} lignes)` : ''}
      </button>
    </section>
  );
}
